import { useEffect, useState } from 'react'
import PortfolioInput, { type PortfolioRow } from './components/PortfolioInput'
import ScreenSelector from './components/ScreenSelector'
import ExposureResults from './components/ExposureResults'
import SupportedFunds from './components/SupportedFunds'
import { analysePortfolio, fetchFunds, type FundSummary } from './lib/api'
import { DEFAULT_SCREENS, type AnalyseResult, type Screen } from './lib/types'

const EXAMPLE_ROWS: PortfolioRow[] = [
  { ticker: 'VTI', allocation: '70' },
  { ticker: 'BND', allocation: '30' },
]

export default function App() {
  const [rows, setRows] = useState<PortfolioRow[]>(EXAMPLE_ROWS)
  const [screens, setScreens] = useState<Screen[]>(DEFAULT_SCREENS)
  const [funds, setFunds] = useState<FundSummary[]>([])
  const [result, setResult] = useState<AnalyseResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchFunds()
      .then(setFunds)
      .catch(() => {
        /* non-fatal: the datalist is a convenience only */
      })
  }, [])

  async function runAnalysis() {
    const portfolio = rows
      .map((r) => ({ ticker: r.ticker.trim(), allocation_pct: parseFloat(r.allocation) || 0 }))
      .filter((r) => r.ticker.length > 0)

    if (portfolio.length === 0) {
      setError('Add at least one fund ticker.')
      return
    }
    if (screens.length === 0) {
      setError('Select at least one ethical screen.')
      return
    }

    setLoading(true)
    setError(null)
    try {
      const res = await analysePortfolio(portfolio, screens)
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>Ethical Portfolio Screener</h1>
        <p className="tagline">
          See what your funds are actually exposed to — fossil fuels, weapons, tobacco and more —
          across your whole portfolio.
        </p>
      </header>

      <main className="layout">
        <div className="inputs">
          <PortfolioInput rows={rows} funds={funds} onChange={setRows} />
          <SupportedFunds funds={funds} />
          <ScreenSelector selected={screens} onChange={setScreens} />
          <button className="analyse-btn" onClick={runAnalysis} disabled={loading}>
            {loading ? 'Analysing…' : 'Analyse portfolio'}
          </button>
          {error && <div className="banner banner-error">{error}</div>}
        </div>

        <div className="output">
          {result ? (
            <ExposureResults result={result} />
          ) : (
            <div className="placeholder">
              <p>Enter your funds and run the analysis to see your weighted exposure.</p>
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <span>Holdings data is approximate and based on recent fund filings. Updated periodically.</span>
      </footer>
    </div>
  )
}
